import { useQuery } from '@tanstack/react-query'
import { getAllSubscriptionTiers, getSubscriptionTiersByType, SubscriptionTierPricing } from '@/api/subscriptionTierPricing'
import { TierInfo } from '@/constants/tiers'

/**
 * Hook to fetch all subscription tiers from backend
 */
export function useAllSubscriptionTiers() {
  return useQuery({
    queryKey: ['subscription-tiers', 'all'],
    queryFn: getAllSubscriptionTiers,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
}

/**
 * Hook to fetch subscription tiers by type
 */
export function useSubscriptionTiersByType(type: 'personal' | 'organization') {
  return useQuery({
    queryKey: ['subscription-tiers', type],
    queryFn: () => getSubscriptionTiersByType(type),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
}

/**
 * Convert backend pricing to frontend TierInfo format
 */
export function convertToTierInfo(pricing: SubscriptionTierPricing): TierInfo {
  // Build features list based on tier type
  const features: string[] = []

  if (pricing.tier_type === 'organization' && pricing.max_members !== undefined) {
    if (pricing.max_members === -1) {
      features.push('∞ членов команды')
    } else {
      features.push(`До ${pricing.max_members} членов команды`)
    }
  }

  if (pricing.max_athletes === -1) {
    features.push('∞ спортсменов')
  } else {
    features.push(`До ${pricing.max_athletes} спортсменов`)
  }

  if (pricing.max_assistants === -1) {
    features.push('∞ направлений')
  } else {
    features.push(`До ${pricing.max_assistants} направлений`)
  }

  if (pricing.max_groups === -1) {
    features.push('∞ групп')
  } else {
    features.push(`До ${pricing.max_groups} групп`)
  }

  if (pricing.max_coaches === -1) {
    features.push('∞ тренеров')
  } else {
    features.push(`До ${pricing.max_coaches} тренеров`)
  }

  if (pricing.max_ai_requests === -1) {
    features.push('∞ AI-запросов')
  } else {
    features.push(`${pricing.max_ai_requests} AI-запросов/мес`)
  }

  // Add enterprise custom terms
  if (pricing.tier === 'enterprise') {
    features.push('Индивидуальные условия')
  }

  // Calculate trial days based on tier
  const trialDays = pricing.tier === 'free' ? 0 :
                    (pricing.tier === 'standard' || pricing.tier === 'team') ? 7 : 14;

  return {
    id: pricing.tier,
    name: pricing.name,
    price: pricing.price_monthly,
    priceYearly: pricing.price_yearly,
    features,
    topFeatures: [], // TODO: Add top features
    maxMembers: pricing.max_members ?? 1, // Default to 1 if not specified
    trialDays, // STANDARD/TEAM: 7 days, PRO/CLUB/PRO_TEAM: 14 days
    highlight: pricing.tier === 'pro' || pricing.tier === 'club', // Highlight popular tiers
  }
}
